import React, { useEffect, useRef, useState } from 'react';
import { Link, NavigateFunction, redirect, useNavigate, useSearchParams } from 'react-router-dom';
import styles from './Signup.module.css';
import eyeOpen from '../../../images/icon-eye-open.svg';
import eyeClose from '../../../images/icon-eye-close.svg';
import { useDispatch } from 'react-redux';
import { getRegister, resetValidationMessage } from '../../../redux/auth/actions/signup.actions';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import { useSelector } from 'react-redux';
import Loader from '../../common/loader/Loader';
import Toast from '../../common/toast/Toast';
import { removeExtraSpace } from '../../../utils/utils';

const Signup = () => {
    const navigate: NavigateFunction = useNavigate();
    const dispatch: AppDispatch = useDispatch();

    const [searchParams, setSearchParams] = useSearchParams();
    let invitedEmail = searchParams.get("email");
    if (invitedEmail) {
        invitedEmail = invitedEmail.trim().replace(/ /g, '+');
    }
    const invitedLocationId = searchParams.get("locationId");
    const invitedAdminId = searchParams.get("adminId");

    const [signupDetails, setSignupDetails] = useState({
        email: '',
        password: '',
        fullName: '',
    });
    const [showPass, setShowPass] = useState(false);
    const [confirmShowPass, setConfirmShowPass] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passError, setPassError] = useState(false);
    const [unvalid, setValidation] = useState(false);
    const fullNameRef = useRef<HTMLInputElement>(null);
    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const confirmPassRef = useRef<HTMLInputElement>(null);

    const signUpError = useSelector((state: IRootState) => state.signupReducer.signUpError);
    const loading = useSelector((state: IRootState) => state.signupReducer.loading);

    const handleSignupDetails = (event: React.FormEvent<HTMLInputElement>) => {
        const { name, value } = event.target as HTMLInputElement;

        if (name == 'password') {
            const regularExpression = /^(?=.*[0-9])(?=.*[- ?!@#$%^&*\\])(?=.*[A-Z])(?=.*[a-z])[a-zA-Z0-9- ?!@#$%^&*\\]{8,30}$/;
            regularExpression.test(value) ? setPassError(false) : setPassError(true);
        }
        if (name == 'fullName') {
            if (event.currentTarget.value[0]?.includes(' ')) {
                event.currentTarget.value = event.currentTarget.value.replace(/\s/g, '');
            }
        }
        setSignupDetails({
            ...signupDetails,
            [name]: removeExtraSpace(value),
        });
    };

    const handleSignup = (e: React.FormEvent) => {
        e.preventDefault();
        if (
            signupDetails.fullName.length === 0 ||
            signupDetails.email.length === 0 ||
            signupDetails.password.length === 0 ||
            signupDetails.password.length > 15 ||
            signupDetails.password !== confirmPassword ||
            passError
        ) {
            setValidation(true);
            fullNameRef.current?.focus();
            signupDetails.fullName.length !== 0 && emailRef.current?.focus();
            signupDetails.fullName.length !== 0 && signupDetails.email.length !== 0 && passwordRef.current?.focus();
            signupDetails.fullName.length !== 0 &&
                signupDetails.email.length !== 0 &&
                signupDetails.password.length !== 0 &&
                confirmPassRef.current?.focus();
            return;
        } else {
            unvalid && setValidation(false);
            if (invitedLocationId && invitedAdminId) {
                dispatch(getRegister(signupDetails, onSuccess, invitedLocationId, invitedAdminId));
            } else {
                dispatch(getRegister(signupDetails, onSuccess));
            }
        }
    };

    const onSuccess = () => {
        if (invitedAdminId && invitedEmail && invitedEmail) {
            navigate(`/invitation${location?.search}`)
        } else {
            navigate('/no-permission');
        }
    };

    useEffect(() => {
        if (searchParams && invitedEmail) {
            setSignupDetails((signupDetails) => {
                return {
                    ...signupDetails,
                    email: invitedEmail && invitedEmail || ''
                }
            })
        }
    }, [searchParams])

    useEffect(() => {
        dispatch(resetValidationMessage());
    }, []);

    return (
        <>
            {signUpError && <Toast message={signUpError} />}
            {loading && <Loader />}
            <div className={styles.contentCenter}>
                <div className={styles.signupWrapper}>
                    <h2 className={styles.signupTitle}>Sign Up to Continue!</h2>
                    <p className={styles.signupInfo}>This is the admin portal sign up for staff.</p>
                    <div className={styles.signupForm}>
                        <form onSubmit={handleSignup} >
                            <div className={styles.formWrapper}>
                                <label>Full Name</label>
                                <input
                                    type="text"
                                    name="fullName"
                                    placeholder="Enter your full name"
                                    onChange={handleSignupDetails}
                                    autoFocus
                                    ref={fullNameRef}
                                />
                                {signupDetails.fullName.length === 0 && unvalid && (
                                    <p className={styles.errorText}>full name should not be empty</p>
                                )}
                            </div>
                            <div className={styles.formWrapper}>
                                <label>Email</label>
                                <input
                                    name="email"
                                    pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
                                    placeholder="Enter your email address"
                                    onChange={handleSignupDetails}
                                    ref={emailRef}
                                    disabled={!!invitedEmail}
                                    readOnly={!!invitedEmail}
                                    value={signupDetails?.email || ''}
                                />
                                {signupDetails.email.length === 0 && unvalid && (
                                    <p className={styles.errorText}>email should not be empty</p>
                                )}
                            </div>
                            <div className={styles.formWrapper}>
                                <label>Password</label>
                                <div className={styles.passwordWrapper}>
                                    <input
                                        type={showPass ? 'text' : 'password'}
                                        name="password"
                                        placeholder="Enter your password"
                                        onChange={handleSignupDetails}
                                        ref={passwordRef}
                                    />
                                    <div className={styles.eyeToggler} onClick={() => setShowPass(!showPass)}>
                                        <img src={!showPass ? eyeClose : eyeOpen} alt="Eye" />
                                    </div>
                                </div>
                                {signupDetails.password.length === 0 && unvalid ? (
                                    <p className={styles.errorText}>Password should not be empty</p>
                                ) : passError ? (
                                    <span className={styles.invalidPassword}>
                                        Password must contain numbers, uppercase, letters, at least one special character, must be greater
                                        than 8 characters
                                    </span>
                                ) : (
                                    ''
                                )}
                            </div>
                            <div className={styles.formWrapper}>
                                <label>Confirm Password</label>
                                <div className={styles.passwordWrapper}>
                                    <input
                                        type={confirmShowPass ? 'text' : 'password'}
                                        name="confirmPassword"
                                        placeholder="Enter your password"
                                        onChange={(e: React.FormEvent<HTMLInputElement>) =>
                                            setConfirmPassword((e.target as HTMLInputElement).value)
                                        }
                                        ref={confirmPassRef}
                                    />
                                    <div className={styles.eyeToggler} onClick={() => setConfirmShowPass(!confirmShowPass)}>
                                        <img src={!confirmShowPass ? eyeClose : eyeOpen} alt="Eye" />
                                    </div>
                                </div>
                                {unvalid && (
                                    <>
                                        {confirmPassword.length === 0 && (
                                            <p className={styles.errorText}>Confirm password should not be empty</p>
                                        )}
                                        {confirmPassword.length > 15 && signupDetails.password === confirmPassword && (
                                            <p className={styles.errorText}>Password must be shorter than or equal to 15 characters</p>
                                        )}
                                        {signupDetails.password.length !== 0 &&
                                            confirmPassword.length !== 0 &&
                                            signupDetails.password !== confirmPassword && (
                                                <p className={styles.errorText}>Password and confirm password do not match</p>
                                            )}
                                    </>
                                )}
                            </div>
                            <div className={styles.formSubmit}>
                                <button type="submit">Sign Up</button>
                            </div>
                            <div className={styles.leadSignup}>
                                <p>
                                    Already have an account?
                                    <Link to={`/${location?.search}`}>Sign In</Link>
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Signup;
